import {
  ITagPosition,
  TagPositionDateGroup,
  TagPositionObjectGroup,
  TagPositionObjectLookup
} from "app/data/types";
import {
  GroupBy,
  getDateKey,
  hasResolvableCoordinates
} from "../reportCommonHelpers";
import { sortBy } from "lodash";
import * as Yup from "yup";

type ResolvedAddress = {
  latitude: number;
  longitude: number;
  address: string;
};

export const findResolvedTagPositionAddress = (
  latitude: number,
  longitude: number,
  resolvedAddresses: ResolvedAddress[] = []
): ResolvedAddress | undefined =>
  resolvedAddresses.find(
    (item) => item.latitude === latitude && item.longitude === longitude
  );

export const getTagPositionLocationLabel = (
  record: Pick<ITagPosition, "resolvedLocation" | "latitude" | "longitude">,
  resolvedAddresses: ResolvedAddress[] = [],
  emptyValue = ""
): string => {
  if (record.resolvedLocation) {
    return record.resolvedLocation;
  }

  if (!hasResolvableCoordinates(record.latitude, record.longitude)) {
    return emptyValue;
  }

  const found = findResolvedTagPositionAddress(
    record.latitude,
    record.longitude,
    resolvedAddresses
  );

  return found?.address || emptyValue;
};

/** True while this coordinate is still waiting for a resolve attempt to finish. */
export const isTagPositionLocationPending = (
  record: Pick<ITagPosition, "resolvedLocation" | "latitude" | "longitude">,
  resolvedAddresses: ResolvedAddress[] = []
): boolean => {
  if (record.resolvedLocation) {
    return false;
  }

  if (!hasResolvableCoordinates(record.latitude, record.longitude)) {
    return false;
  }

  // No store entry yet means the request is still in flight (or not started).
  return !findResolvedTagPositionAddress(
    record.latitude,
    record.longitude,
    resolvedAddresses
  );
};

//Tag Positions
export const reportsTagFilterFormInitialValues = {
  objectType: [],
  group: [],
  object: [],
  daysOfWeek: ["0", "1", "2", "3", "4", "5", "6"],
  startTime: "00:00",
  endTime: "23:59",
  assignedToAllObjects: true
};

export const reportsTagFilterFormValidationSchema: Yup.Schema = Yup.object({
  objectType: Yup.array().of(Yup.string()).label("Object Types"),
  group: Yup.array().of(Yup.string()).label("Groups"),
  assignedToAllObjects: Yup.boolean(),
  object: Yup.array()
    .of(Yup.string())
    .label("Objects")
    .when("assignedToAllObjects", {
      is: false,
      then: (schema) =>
        schema.min(1, "At least one object must be selected").required(),
      otherwise: (schema) => schema
    })
});

type TagPositionObjectWip = {
  objectUniqueId: string;
  name: string;
  aNumber: string;
  detailsByDate: Map<string, TagPositionDateGroup>;
};

const earliestRecordTime = (detail: TagPositionDateGroup): number | null => {
  let min: number | null = null;
  for (const record of detail.records) {
    const time = new Date(record.date).getTime();
    if (!Number.isNaN(time) && (min === null || time < min)) {
      min = time;
    }
  }
  return min;
};

const compareDetailsByEarliestRecord = (
  a: TagPositionDateGroup,
  b: TagPositionDateGroup
): number => {
  const aTime = earliestRecordTime(a);
  const bTime = earliestRecordTime(b);

  if (aTime !== null && bTime !== null) {
    if (aTime !== bTime) {
      return aTime - bTime;
    }
    return a.label.localeCompare(b.label);
  }
  if (aTime !== null) {
    return -1;
  }
  if (bTime !== null) {
    return 1;
  }
  return a.label.localeCompare(b.label);
};

export const buildTagPositionObjectLookup = (
  objects: TagPositionObjectLookup[] = []
): Map<string, TagPositionObjectLookup> => {
  const lookup = new Map<string, TagPositionObjectLookup>();

  objects.forEach((object) => {
    if (object?.uniqueId) {
      lookup.set(object.uniqueId, object);
    }
  });

  return lookup;
};

export const organizeData = (
  data: ITagPosition[],
  groupBy: GroupBy,
  objectLookup: Map<string, TagPositionObjectLookup> = new Map()
): TagPositionObjectGroup[] => {
  if (!data?.length) {
    return [];
  }

  const byObjectId = new Map<string, TagPositionObjectWip>();

  data.forEach((entry: ITagPosition) => {
    // BE sends object UniqueId in serialNumber
    const objectUniqueId = entry.serialNumber || "N/A";
    let objectEntry = byObjectId.get(objectUniqueId);
    const matchedObject = objectLookup.get(objectUniqueId);

    if (!objectEntry) {
      objectEntry = {
        objectUniqueId,
        name: matchedObject?.name || entry.name || "N/A",
        aNumber: matchedObject?.aNumber || "N/A",
        detailsByDate: new Map()
      };
      byObjectId.set(objectUniqueId, objectEntry);
    } else {
      if (objectEntry.name === "N/A") {
        objectEntry.name = matchedObject?.name || entry.name || "N/A";
      }
      if (objectEntry.aNumber === "N/A" && matchedObject?.aNumber) {
        objectEntry.aNumber = matchedObject.aNumber;
      }
    }

    const dateKey = getDateKey(entry.date, groupBy);
    let day = objectEntry.detailsByDate.get(dateKey);

    if (!day) {
      day = { label: dateKey, records: [] };
      objectEntry.detailsByDate.set(dateKey, day);
    }

    day.records.push(entry);
  });

  const formattedData: TagPositionObjectGroup[] = Array.from(
    byObjectId.values()
  ).map(({ objectUniqueId, name, aNumber, detailsByDate }) => {
    const details = Array.from(detailsByDate.values()).map((day) => ({
      label: day.label,
      records: [...day.records].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
    }));

    details.sort(compareDetailsByEarliestRecord);

    return {
      objectUniqueId,
      name,
      aNumber,
      positionCount: details.reduce((acc, day) => acc + day.records.length, 0),
      details
    };
  });

  return sortBy(formattedData, ["name", "aNumber", "objectUniqueId"]);
};
